/**
 * 操作履歴（監査ログ）の一覧。
 *
 * `GET /admin/audit-logs` を新しい順に並べる。続きは、返ってきた続きの位置を
 * そのまま次の呼び出しに渡す（件数で数えないので、見ている間に記録が増えても
 * 同じ行を二度見たり飛ばしたりしない）。
 *
 * 記録に入っているのはメタ情報だけで、メッセージの本文は含まれない（docs/01 15-2）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useApi } from '../api/context.js';
import type { AuditLogPage } from '../api/types.js';
import { auditActionLabel } from '../audit/action-labels.js';
import { actorLabel, formatTarget } from '../audit/format.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { Button } from '../components/ui/Button.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { formatTableDateTime } from '../format/datetime.js';

/** 1ページに出す件数。 */
const PAGE_SIZE = 50;

export function AuditLogsPage(): ReactNode {
  const api = useApi();
  // いままでに開いたページの「続きの位置」。先頭ページは位置なし
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [page, setPage] = useState<AuditLogPage | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const before = cursors[pageIndex];

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError(null);
    api
      .get<AuditLogPage>('/admin/audit-logs', {
        query: { limit: PAGE_SIZE, ...(before !== undefined ? { before } : {}) },
      })
      .then((loaded) => {
        if (!cancelled) setPage(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, before, reloadKey]);

  function showNext(): void {
    const next = page?.nextCursor;
    if (!next) return;
    setCursors((prev) => [...prev.slice(0, pageIndex + 1), next]);
    setPageIndex((index) => index + 1);
  }

  function showPrevious(): void {
    setPageIndex((index) => Math.max(0, index - 1));
  }

  return (
    <article>
      <PageHeader
        title="操作履歴"
        description="管理の操作を新しい順に並べています。記録は後から書き換えられません。メッセージの本文やファイルの中身は含みません。"
      />

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !page ? <LoadingBlock /> : null}

      {page && page.items.length === 0 ? <EmptyBlock label="記録はまだありません。" /> : null}

      {page && page.items.length > 0 ? (
        <DataTable label="操作履歴の一覧">
          <thead>
            <tr>
              <th scope="col">日時</th>
              <th scope="col">操作</th>
              <th scope="col">操作した方</th>
              <th scope="col">対象</th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((item) => (
              <tr key={item.id}>
                <td className="ui-cell-date">{formatTableDateTime(item.createdAt)}</td>
                <td className="ui-cell-wrap">{auditActionLabel(item.action, item.target)}</td>
                <td>{actorLabel(item, page.names)}</td>
                <td className="ui-cell-wrap">{formatTarget(item, page.names)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}

      <nav className="admin-pager" aria-label="ページ送り">
        <Button variant="secondary" compact onClick={showPrevious} disabled={pageIndex === 0}>
          前へ
        </Button>
        <span>{pageIndex + 1}ページ目</span>
        <Button variant="secondary" compact onClick={showNext} disabled={!page?.nextCursor}>
          次へ
        </Button>
      </nav>
    </article>
  );
}
